import { useEffect, useState, useRef } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import ReportsService, {
	ContainerReportProps,
	ShipStats,
} from '../../services/Reports.service';
import { useTranslation } from 'react-i18next';
import MonitorLayout from '../home/reports/monitor-layout';
import Button from '../../components/atoms/button';
import { dateObjToStr } from '../../utils/date';
import Text from '../../components/atoms/text';
import { exportToExcel } from '../../utils/exportToExcel';
import { HomeLoaderData } from '../../types/homeLoaderData';

const ContainerReports = () => {
	const { user } = useLoaderData() as HomeLoaderData;
	const supplyReturnAtivado =
		user.client?.system_configs?.supplyReturnAtivado === true;
	const [searchParams] = useSearchParams();
	const [reportData, setReportData] = useState<ContainerReportProps[]>([]);
	const [loading, setLoading] = useState(false);
	const [isPrinting, setIsPrinting] = useState(false);
	const printableRef = useRef<HTMLDivElement>(null);
	const paginaRef = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();
	const [date, setDate] = useState('');
	const [isFinance, setIsFinance] = useState(false);
	const [shipStats, setShipStats] = useState<ShipStats[]>([]);

	useEffect(() => {
		const pagina = paginaRef.current;
		if (!pagina) return;
		let larguraAnterior = 0;
		let quadro = 0;

		const observador = new ResizeObserver(([entrada]) => {
			const larguraDisponivel = entrada.contentRect.width;
			if (!larguraDisponivel || larguraDisponivel === larguraAnterior) return;
			larguraAnterior = larguraDisponivel;
			cancelAnimationFrame(quadro);
			// Aplica o zoom fora da notificação para não gerar um ciclo de resize.
			quadro = requestAnimationFrame(() => {
				// A tabela precisa de 900px, além dos 128px de padding da página.
				const larguraRelatorio = Math.max(1028, larguraDisponivel);
				pagina.style.setProperty('--largura-relatorio', `${larguraRelatorio}px`);
				pagina.style.setProperty('--escala-relatorio', `${larguraDisponivel / larguraRelatorio}`);
			});
		});
		observador.observe(pagina);
		return () => {
			observador.disconnect();
			cancelAnimationFrame(quadro);
		};
	}, []);

	useEffect(() => {
		const loadData = async () => {
			const savedData = sessionStorage.getItem('report');
			if (savedData) {
				const data = JSON.parse(savedData) as ContainerReportProps[];
				setReportData(data);
				return;
			}

			setLoading(true);
			const params = {
				dateBegin: searchParams.get('dateBegin') || '',
				dateEnd: searchParams.get('dateEnd') || '',
				ship: searchParams.get('ship') || '',
				onlyAlert: searchParams.get('onlyAlert') || '',
				isFinance: searchParams.get('isFinance') || '',
				travelCode: searchParams.get('travelCode') || '',
				container: searchParams.get('container') || '',
				accountId: searchParams.get('accountId') || '',
				yard: searchParams.get('yard') || '',
			};
			if (params.dateBegin && params.dateEnd) {
				setDate(
					`${dateObjToStr(params.dateBegin)} - ${dateObjToStr(params.dateEnd)}`
				);
			}

			const response = await ReportsService.filteredReports({
				idClient: Number(params.accountId),
				yard: params.yard,
				ship: params.ship !== t('misc.all') ? params.ship : undefined,
				container:
					params.container !== t('misc.all')
						? params.container
						: undefined,
				travelCode:
					params.travelCode !== t('misc.all')
						? params.travelCode
						: undefined,
				startDate: new Date(params.dateBegin),
				endDate: new Date(params.dateEnd),
			});

			if (!response || !response.data || response.data.length == 0)
				return;
			let finalData = [];
			if (params.onlyAlert === '1') {
				for (let report of response.data) {
					let newReport = [];
					for (let monitor of report.monitors) {
						if (monitor.errors.length > 0) {
							newReport.push(monitor);
						}
					}
					if (newReport.length > 0) {
						finalData.push({ ...report, monitors: newReport });
					}
				}
			} else {
				finalData = [...response.data];
			}
			if (params.isFinance === '1') {
				setIsFinance(true);
			}
			setReportData(finalData);
			setShipStats(response.shipStats);
			setLoading(false);
		};

		loadData();
	}, [searchParams, t]);

	const handlePrint = () => {
		setIsPrinting(true);

		// Pequeno delay para garantir que o CSS de impressão seja aplicado
		setTimeout(() => {
			window.print();
		}, 100);
	};

	// Limpa o estado de impressão após o print
	useEffect(() => {
		const afterPrint = () => {
			setIsPrinting(false);
		};

		window.addEventListener('afterprint', afterPrint);
		return () => {
			window.removeEventListener('afterprint', afterPrint);
		};
	}, []);

	const exportMirror = () => {
		// Verificar se há dados
		if (!reportData || reportData.length === 0) {
			return;
		}

		// Mapear os dados
		const data = reportData.map((container) => ({
			Container: container.label || '',
			'Viagem de Entrada': container.monitors?.[0]?.travel || '',
			SetPoint: container.monitors?.[0]?.setpoint || '',
			Total: container.monitors?.length || 0,
			Falhas:
				container.monitors?.flatMap((m) => m.errors || []).length || 0,
			Evidências:
				container.monitors?.flatMap((m) => m.images || []) || [],
		}));

		// Exportar diretamente como array de objetos
		exportToExcel(data, {
			fileName: `relatorio_espelho_reefersys_${new Date().getTime()}`,
			sheetName: 'Relatório',
			imageColumns: ['Evidências'],
		});
	};

	const exportTotal = () => {
		// Verificar se há dados
		if (!reportData || reportData.length === 0) {
			return;
		}

		// Mapear os dados
		const data = [];
		for (let container of reportData) {
			for (let monitor of container.monitors) {
				data.push({
					Container: container.label || '',
					Data: dateObjToStr(monitor.date),
					'Entrada - Navio - Viagem': `${dateObjToStr(container.date_begin)} - ${monitor.ship} - ${monitor.travel}`,
					'Saída - Navio - Viagem': container.date_end
						? `${dateObjToStr(container.date_end)} - ${monitor.ship} - ${monitor.travel}`
						: '',
					SetPoint: monitor.setpoint,
					...(supplyReturnAtivado
						? { Supply: monitor.supply ?? '-', Return: monitor.return ?? '-' }
						: { Temperatura: monitor.temperature }),
					Consig: monitor.owner,
					Posição: monitor.position,
					Evidências: monitor.images,
				});
			}
		}

		// Exportar diretamente como array de objetos
		exportToExcel(data, {
			fileName: `relatorio_completo_reefersys_${new Date().getTime()}`,
			sheetName: 'Relatório',
			imageColumns: ['Evidências'],
		});
	};

	return (
		<div ref={paginaRef}>
			<div className="relatorio-conteudo flex flex-col gap-4 p-16 print:p-0">
				<div className="flex justify-end gap-4 print:hidden">
					<Button onClick={exportMirror}>Exportar Espelho (XLSX)</Button>
					<Button onClick={exportTotal}>Exportar Completo (XLSX)</Button>
					<Button
						className=""
						onClick={handlePrint}
						disabled={loading || reportData.length === 0}
					>
						{t('financial.exportPDF')}
					</Button>
				</div>
				<div className="flex justify-end">
					<Text className="font-semibold text-sm">{date}</Text>
				</div>
				{/* Conteúdo que será impresso */}
				<div
					ref={printableRef}
					className={`print-content ${isPrinting ? 'printing' : ''}`}
				>
					<MonitorLayout
						data={reportData}
						isFinance={isFinance}
						shipStats={shipStats}
						supplyReturnAtivado={supplyReturnAtivado}
					/>
				</div>
			</div>
		</div>
	);
};

export default ContainerReports;
